/* ============================================================
 * Kandy Slide Cart
 * ------------------------------------------------------------
 * All cart writes flow through a single serialized queue and a
 * single-flight rebuild so quantity/remove actions can never race
 * each other or the shipping-protection reconcile. Every handler is
 * delegated so it survives the drawer being re-rendered.
 * ========================================================== */
(function ($) {
  if (!$ || window.__kandyCartInit) return;
  window.__kandyCartInit = true;

  // Safe logger: tolerate window.log being absent so a missing util
  // can never break cart interactions.
  var log = (typeof window.log === 'function') ? window.log : function () {};
  if (typeof log.collapse !== 'function') log.collapse = function () {};
  if (typeof log.table !== 'function') log.table = function () {};

  var DRAWER = '#cart-drawer-kandy';
  var EXCLUDED_PRODUCT_IDS = [10280370667830];
  var SP_STORAGE_KEY = 'shippingProtectionDisabled';
  var SP_TTL_MS = 60 * 60 * 1000; // 1 hour

  /* ================= Cart API (promise-based) ================= */
  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  function cartAdd(items) {
    return request('/cart/add.js', { items: items });
  }

  // Only strip undefined/null — keep '' (removes a selling plan) and 0 (removes a line).
  function cartChange(body) {
    var clean = {};
    Object.keys(body).forEach(function (k) {
      if (body[k] !== undefined && body[k] !== null) clean[k] = body[k];
    });
    return request('/cart/change.js', clean);
  }

  function cartUpdate(updates) {
    return request('/cart/update.js', { updates: updates });
  }

  /* ================= Serialized mutation queue ================= */
  // Every user action is appended to a single promise chain, so writes
  // are applied one at a time in order. A failed op is logged and does
  // not stall the queue.
  var chain = Promise.resolve();

  function enqueue(task) {
    chain = chain.then(task).catch(function (err) {
      log.collapse(err, 'Cart operation failed', 'error');
    });
    return chain;
  }

  // commit(writeFn): run one cart write (or none), then reconcile
  // shipping protection, then rebuild the drawer once — all serialized.
  function commit(writeFn) {
    return enqueue(function () {
      return Promise.resolve(writeFn ? writeFn() : null)
        .then(getCart)
        .then(reconcileShippingProtection)
        .then(rebuildView);
    });
  }

  /* ================= Single-flight rebuild ================= */
  // Fetches the freshly rendered drawer markup and swaps it in. If a
  // rebuild is requested while one is running, it is coalesced into a
  // single follow-up pass instead of stacking.
  var rebuilding = false;
  var rebuildQueued = false;

  function fetchDrawerHtml() {
    // Prefer the Section Rendering API (fast, drawer-only); fall back to
    // the full page if it is unavailable.
    return fetch('/?sections=cart_drawer', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && typeof data.cart_drawer === 'string') return data.cart_drawer;
        throw new Error('no section html');
      })
      .catch(function () {
        return fetch(window.location.pathname).then(function (r) { return r.text(); });
      });
  }

  function rebuildView(cart) {
    if (rebuilding) {
      rebuildQueued = true;
      return Promise.resolve(cart);
    }
    rebuilding = true;

    return fetchDrawerHtml()
      .then(function (html) {
        // Parse WITHOUT executing scripts. jQuery's $(html)/.html() re-runs
        // inline <script> tags found in the drawer markup (e.g. the
        // `const productSellingPlans` block in cart_drawer-items), which throws
        // a "redeclaration" SyntaxError and aborts the rebuild. DOMParser and
        // innerHTML never execute scripts, and those globals are already set
        // from the initial page load, so nothing needs to re-run here.
        var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(DRAWER);
        var current = document.querySelector(DRAWER);
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        updateCartCount(cart);
        initTransientWidgets();
        updateShippingRow(cart);
        document.dispatchEvent(new CustomEvent('cart:rebuilt', { detail: cart || null }));
      })
      .catch(function (err) {
        log.collapse(err, 'Failed to rebuild cart drawer', 'error');
      })
      .then(function () {
        rebuilding = false;
        if (rebuildQueued) {
          rebuildQueued = false;
          return rebuildView(cart);
        }
      });
  }

  function updateCartCount(cart) {
    if (!cart || !cart.items) return;
    var count = 0;
    cart.items.forEach(function (item) {
      if (EXCLUDED_PRODUCT_IDS.indexOf(item.product_id) === -1) count += item.quantity;
    });
    $('.cart-count-bubble').text(count);
  }

  // Re-initialize widgets whose DOM is replaced on every rebuild.
  function initTransientWidgets() {
    applyShippingProtectionState();
    initUpsellCarousel();
    if ($('.cd__timer').length) startCountdown();
    setTimeout(function () { $('.cd-upsells__out').addClass('active'); }, 300);
  }

  /* ================= Shipping estimator row ================= */
  // Lives here (not in the drawer snippet) so it runs on every rebuild — the
  // snippet's inline <script> only executes on a full page load. Location comes
  // from Shopify localization (stored by theme.liquid as nb__region_code); no
  // third-party geo lookup, so it can never flicker.
  var FREE_SHIPPING_THRESHOLD = 99; // dollars
  var SHIPPING_COST = 4.95;

  function getUserLocation() {
    try { return localStorage.getItem('nb__region_code') || 'US'; } catch (e) { return 'US'; }
  }

  function updateShippingRow(cart) {
    var container = document.querySelector('.cd__footer-prices');
    if (!container) return;

    // Drop the static Liquid row ("Calculated at next step") in favor of ours.
    var liquidRow = container.querySelector('[data-value="shipping"]:not(#custom-shipping-row)');
    if (liquidRow) liquidRow.remove();

    var row = container.querySelector('#custom-shipping-row');
    if (!row) {
      row = document.createElement('div');
      row.id = 'custom-shipping-row';
      row.className = 'cd__footer-prices-line';
      row.setAttribute('data-value', 'shipping');
      row.innerHTML = '<span class="label">Shipping To: <span class="shipping-state">' + getUserLocation() +
        '</span></span><span class="value"></span>';
      var totalRow = container.querySelector('[data-value="total"]');
      if (totalRow) container.insertBefore(row, totalRow); else container.appendChild(row);
    }

    var valueEl = row.querySelector('.value');
    var stateEl = row.querySelector('.shipping-state');
    var totalEl = document.querySelector('[data-value="total"] .value');
    if (!valueEl || !stateEl || !totalEl) return;

    var apply = function (c) {
      if (!c || !c.items) return;
      var subtotal = c.items_subtotal_price / 100;
      var freeShipping = c.items.some(function (i) { return i.selling_plan_allocation; }) || subtotal >= FREE_SHIPPING_THRESHOLD;
      stateEl.textContent = getUserLocation();
      valueEl.textContent = freeShipping ? 'FREE' : '$' + SHIPPING_COST.toFixed(2);
      totalEl.textContent = '$' + (subtotal + (freeShipping ? 0 : SHIPPING_COST)).toFixed(2);
    };

    if (cart && cart.items) apply(cart); else getCart().then(apply);
  }

  /* ================= Public entry points ================= */
  // Kept for backwards-compatibility with the many other scripts that
  // call window.cart_open / cart_build / cart_close and postToCart.
  function postToCart(items, successCallback, loadingCallback, failCallback) {
    if (loadingCallback) loadingCallback();
    return enqueue(function () {
      return cartAdd(items)
        .then(getCart)
        .then(reconcileShippingProtection)
        .then(rebuildView)
        .then(function () {
          if (successCallback) successCallback(); else openSlideCart();
        })
        .catch(function (err) {
          log.collapse(err, 'Add to cart failed', 'error');
          if (failCallback) failCallback(); else alert('Error adding items to the cart.');
        });
    });
  }

  function buildCart() {
    return commit();
  }

  /* ================= Cart item quantity / remove ================= */
  function parseItemProperties($item) {
    var raw = $item.find('.cd__item-properties').attr('data-properties');
    if (!raw) return {};
    try { return JSON.parse(raw); }
    catch (e) {
      try { return JSON.parse(decodeURIComponent(raw)); } catch (err) { return {}; }
    }
  }

  // Announce a manual removal so other scripts (e.g. subscription bundle
  // sync) can treat the deletion as authoritative and not re-add it.
  function notifyLineRemoved(variantId) {
    if (!variantId) return;
    document.dispatchEvent(new CustomEvent('cart:line-removed', { detail: { variantId: Number(variantId) } }));
  }

  function updateCartQuantity(action, element) {
    var $item = $(element).closest('.cd__item');
    $item.addClass('loading');

    var id = $item.attr('data-id');
    var key = $item.attr('data-key') || id; // line key is the robust identifier

    // Removing/decrementing shipping protection = user opted out.
    var shipProtId = $('.cd__ship-prot').data('id');
    if (id == shipProtId && (action === 'remove' || action === 'minus')) {
      $('.cd__ship-prot-checking').addClass('disabled');
      setShippingProtectionDisabled();
    }

    var quantity = parseFloat($item.find('.cd__item-quantity-value').text()) || 0;
    if (action === 'plus') quantity++;
    if (action === 'minus') quantity--;
    if (action === 'remove') quantity = 0;

    // Removing a bundle line removes every line sharing its _bundle_id.
    if ((action === 'remove' || action === 'minus') && quantity <= 0) {
      var bundleId = parseItemProperties($item)._bundle_id;
      if (bundleId) {
        var updates = {};
        $('.cd__item').each(function () {
          var $other = $(this);
          var match = $other.find('.cd__item-properties').text().match(/_bundle_id:\s*([^\s]+)/);
          if (match && match[1] === bundleId) {
            updates[$other.attr('data-key') || $other.attr('data-id')] = 0;
            notifyLineRemoved($other.attr('data-id'));
          }
        });
        if (Object.keys(updates).length > 1) {
          commit(function () { return cartUpdate(updates); });
          return;
        }
      }
    }

    if (quantity <= 0) notifyLineRemoved(id);
    commit(function () { return cartChange({ id: key, quantity: quantity }); });
  }

  $(document)
    .on('click', '.cd__item-quantity-plus', function () { updateCartQuantity('plus', this); })
    .on('click', '.cd__item-quantity-minus', function () { updateCartQuantity('minus', this); })
    .on('click', '.cd__item-remove', function () { updateCartQuantity('remove', this); });

  /* ================= Subscription upgrade ================= */
  $(document).on('click', '.cd__item-upgrade-btn, .cd__sub-toggle', function () {
    var $btn = $(this);
    var isSubToggle = $btn.hasClass('cd__sub-toggle');
    var wasActive = $btn.hasClass('active');

    if ($btn.closest('.cd__item-upgrade-wrapper').hasClass('pill-upgrade')) $btn.toggleClass('active');
    if (isSubToggle) $btn.toggleClass('active');

    var sellingPlan = (isSubToggle && wasActive) ? '' : $btn.data('plan-id');

    $btn.closest('.cd__item').addClass('loading');
    commit(function () {
      return cartChange({ id: $btn.data('id'), quantity: $btn.data('quantity') || undefined, selling_plan: sellingPlan });
    });
  });

  $(document).on('click', '.cd__item-upgrade-sub', function () {
    var $btn = $(this);
    $btn.closest('.cd__item').addClass('loading');

    var removeId = $btn.data('id');
    var addId = $btn.data('upgrade-id');
    var totalQty = +$btn.data('quantity');
    var sellingPlan = $btn.data('plan-id');

    commit(function () {
      return cartChange({ id: removeId, quantity: 0 })
        .then(getCart)
        .then(function (cart) {
          var existing = cart.items.find(function (item) {
            return item.id == addId && (!item.selling_plan_allocation || item.selling_plan_allocation.selling_plan_id == sellingPlan);
          });
          if (existing) {
            return cartChange({ id: existing.key, quantity: totalQty, selling_plan: sellingPlan });
          }
          return cartAdd([{ id: addId, quantity: totalQty, selling_plan: sellingPlan }]);
        });
    });
  });

  /* ================= Drawer open / close ================= */
  $(document).on('click',
    '.header__icon-list a[href="/cart"][aria-controls="cart-drawer"], a[href="/cart"].header__icon, #cart-icon-bubble',
    function (evt) { evt.preventDefault(); openSlideCart(); }
  );

  $(document).on('click', '.cd__outer', function (evt) {
    var $target = $(evt.target);
    if (!$target.closest('.cd__inner').length || $target.closest('.cd__nav-back').length) {
      closeSlideCart();
    }
  });

  $(document).on('click', '.cd__checkout-btn', function (evt) {
    evt.preventDefault();
    window.location.href = '/checkout';
  });

  $(document)
    .on('mouseover', '.cd__item-remove', function (e) { $(e.target).addClass('active'); })
    .on('mouseleave', '.cd__item-remove', function (e) { $(e.target).removeClass('active'); });

  /* ================= Shipping protection ================= */
  function setShippingProtectionDisabled() {
    try {
      localStorage.setItem(SP_STORAGE_KEY, JSON.stringify({ value: 'true', expiry: Date.now() + SP_TTL_MS }));
    } catch (e) {}
  }

  function isShippingProtectionDisabled() {
    try {
      var stored = localStorage.getItem(SP_STORAGE_KEY);
      if (!stored) return false;
      var parsed = JSON.parse(stored);
      if (parsed.value === 'true' && Date.now() < parsed.expiry) return true;
      localStorage.removeItem(SP_STORAGE_KEY);
    } catch (e) {}
    return false;
  }

  // Re-apply the user's opt-out onto freshly rendered markup (the server
  // always renders the default state).
  function applyShippingProtectionState() {
    if (isShippingProtectionDisabled()) $('.cd__ship-prot-checking').addClass('disabled');
  }
  window.applyShippingProtectionState = applyShippingProtectionState;

  // Decide whether the shipping-protection line should be present and, if
  // needed, perform exactly one update. Never touches any other product,
  // so it can never re-add a line the shopper just removed.
  function reconcileShippingProtection(cart) {
    var $sp = $('.cd__ship-prot');
    var dataId = $sp.data('id');
    if (!dataId || !cart || cart.item_count === 0) return cart;

    var spItem = cart.items.find(function (item) { return item.id == dataId; });
    var hasOtherItems = cart.items.some(function (item) { return item.id != dataId; });
    var isChecked = !$('.cd__ship-prot-checking').hasClass('disabled');
    var enabledDefault = $sp.attr('data-protection') === 'true';

    var shouldHaveProtection;
    if (isShippingProtectionDisabled() || !hasOtherItems) {
      shouldHaveProtection = false;
    } else {
      shouldHaveProtection = isChecked || enabledDefault;
    }

    var currentQty = spItem ? spItem.quantity : 0;
    var targetQty = shouldHaveProtection ? 1 : 0;
    if (currentQty === targetQty) return cart;

    return cartUpdate({ [dataId]: targetQty }).then(getCart);
  }

  $(document).on('click', '.cd__ship-prot-checking', function () {
    var wasDisabled = $(this).hasClass('disabled');
    $(this).toggleClass('disabled');
    if (wasDisabled) {
      try { localStorage.removeItem(SP_STORAGE_KEY); } catch (e) {}
    } else {
      setShippingProtectionDisabled();
    }
    commit(); // reconcile + rebuild
  });

  // Opting out from the full cart page.
  $(document).on('click', '.cd__ship-prot [href^="/cart/change?id="], a[href^="/cart/change?id="]', function () {
    var dataId = $('.cd__ship-prot').data('id');
    if (dataId && this.getAttribute('href').indexOf('id=' + dataId) !== -1) {
      $('.cd__ship-prot-checking').addClass('disabled');
      setShippingProtectionDisabled();
    }
  });

  /* ================= Discount / gift progress bar ================= */
  $(document).on('click', '.discount-bar__gift-header-close', function () {
    $('discount-progress-bar-gift').hide();
  });

  $(document).on('click', '.cd__gift-selector-modal .modal-close', function () {
    $('.cd__gift-selector-modal').removeClass('active').hide();
  });

  $(document).on('click', '.cd__gift-selector-modal-atc', function () {
    var $btn = $(this);
    var selectedColor = $('.cd__gift-selector-modal-selector[data-type="color"] option:selected').val();
    var selectedSize = $('.cd__gift-selector-modal-selector[data-type="size"] option:selected').val();
    var selectedId = $('.cd__gift-variant[data-color="' + selectedColor + '"][data-size="' + selectedSize + '"]').data('id') || $btn.data('id');

    postToCart(
      [{ id: selectedId, quantity: 1 }],
      function () {
        $('.cd__gift-selector-modal').removeClass('active').hide();
        $('.cd__gift-selector-modal-atc').removeClass('loading');
      },
      function () { $('.cd__gift-selector-modal-atc').addClass('loading'); },
      function () { $('.cd__gift-selector-modal-atc').removeClass('loading'); alert('Error adding gift to cart'); }
    );
  });

  /* ================= Upsells ================= */
  function initUpsellCarousel() {
    var $carousel = $('.cd-upsells__carousel');
    if (!$carousel.length || typeof $carousel.slick !== 'function') return;
    // DOM was replaced on rebuild, so re-initialize a fresh instance.
    if ($carousel.hasClass('slick-initialized')) {
      try { $carousel.slick('unslick'); } catch (e) {}
    }
    $carousel.slick({
      autoplay: true,
      autoplaySpeed: 3000,
      infinite: true,
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: true,
      dots: false,
      prevArrow: "<button type='button' class='slick-prev pull-left'><img class='left_arrow' src='https://cdn.shopify.com/s/files/1/0679/7135/0705/files/Chevron_Left.png?v=1737122129'/></button>",
      nextArrow: "<button type='button' class='slick-next pull-right'><img class='right_arrow' src='https://cdn.shopify.com/s/files/1/0679/7135/0705/files/Chevron_Right.png?v=1737122129'/></button>"
    });
  }

  $(document).on('click', '.cd-upsells__toggle', function () {
    $('.cd-upsells__out').toggleClass('active');
  });

  $(document).on('change', '.cd-upsells__block-selector', function () {
    var $selector = $(this);
    var currentId = $selector.val();
    var $parentBlock = $selector.closest('.cd-upsells__block-wrapper');
    $parentBlock.find('.cd-upsells__block-atc').attr('data-id', currentId);

    var currency = ($('.cd__checkout-btn').attr('data-cur') || '').replace('0,00', '').replace('0.00', '');
    var currentPrice = $selector.find('option:selected').data('price');
    $parentBlock.find('.cd-upsells__block-price .pri').html(currency + (currentPrice / 100).toFixed(2));

    var currentComparePrice = $selector.find('option:selected').data('compare-price');
    if (currentComparePrice) {
      var currentDiscount = Math.round((currentComparePrice - currentPrice) * 100 / currentComparePrice);
      $parentBlock.find('.cd-upsells__block-price s').html(currentComparePrice);
      $parentBlock.find('.cd-upsells__block-price .badge').html(currentDiscount + '% OFF');
    }
  });

  $(document).on('click', '.cd-upsells__block-atc', function (evt) {
    evt.preventDefault();
    var currentId = $(this).attr('data-id');
    // Cart is already open here; rebuild only, don't re-trigger the open animation.
    postToCart([{ id: currentId, quantity: 1 }], function () {});
  });

  /* ================= Reserved timer ================= */
  var updateTimer;
  function startCountdown() {
    if (updateTimer) clearInterval(updateTimer);

    var minutes = $('.cd__timer').data('timer');
    var countDownDate = new Date(Date.now() + minutes * 60 * 1000);

    updateTimer = setInterval(function () {
      var distance = countDownDate - new Date();
      if (distance <= 0) {
        clearInterval(updateTimer);
        $('.cd_timer-reserved__hour, .cd_timer-reserved__min, .cd_timer-reserved__sec').text('0');
        $('.cd__timer-reserved p').text('Time is out!');
        fetch('/cart/clear.js', { method: 'POST' }).then(function () { commit(); });
      } else {
        $('.cd_timer-reserved__hour').text(Math.floor((distance / (1000 * 60 * 60)) % 24));
        $('.cd_timer-reserved__min').text(Math.floor((distance / (1000 * 60)) % 60));
        $('.cd_timer-reserved__sec').text(Math.floor((distance / 1000) % 60));
      }
    }, 1000);
  }

  /* ================= Add-to-cart -> open drawer ================= */
  $(document).on('click', "buy-buttons button[type='submit']", function () {
    var $btn = $(this);
    var originalText = $btn.html();
    $btn.html('Adding to Cart...');
    setTimeout(function () {
      openSlideCart();
      commit();
      setTimeout(function () { $btn.html(originalText); }, 1000);
    }, 1500);
  });

  $(document).on('click', '.kv-add-to-cart', function () {
    var $btn = $(this);
    var $textEl = $btn.find('.kv-add-to-cart_text');
    var $priceEl = $btn.find('.kv-price');
    var originalText = $textEl.text();
    $textEl.text('Adding to Cart...');
    $priceEl.hide();

    var $form = $btn.closest('form');
    var variantId = $form.find('input[name="id"]').val();
    var sellingPlanId = $form.find('input[name="selling_plan"]').val();
    var quantity = $form.find('input[name="quantity"]').val();

    postToCart(
      [{ id: variantId, quantity: quantity, selling_plan: sellingPlanId }],
      function () {
        openSlideCart();
        setTimeout(function () { $textEl.text(originalText); $priceEl.show(); }, 1000);
      }
    );
  });

  /* ================= Global open/close ================= */
  function openSlideCart() {
    $('.cd__outer').css('display', 'flex');
    setTimeout(function () {
      $('body').addClass('Overflow_hidden').addClass('overflow-hidden');
      $('.cd__outer').addClass('active');
      var $carousel = $('.cd-upsells__carousel');
      if ($carousel.length && typeof $carousel.slick === 'function' && $carousel.hasClass('slick-initialized')) {
        $carousel.slick('refresh');
      }
    }, 100);
  }

  function closeSlideCart() {
    $('.cd__outer').removeClass('active');
    setTimeout(function () {
      $('.cd__outer').hide();
      $('body').removeClass('Overflow_hidden').removeClass('overflow-hidden');
    }, 300);
  }

  /* ================= Init ================= */
  $(function () {
    // Seed the shipping-protection checkbox from the default + stored state.
    var enabledDefault = $('.cd__ship-prot').attr('data-protection') === 'true';
    if (!enabledDefault || isShippingProtectionDisabled()) {
      $('.cd__ship-prot-checking').addClass('disabled');
    }
    initUpsellCarousel();
    if ($('.cd__timer').length) startCountdown();
    updateShippingRow(); // render the estimator on first load (items already in cart)
  });

  // Public API (unchanged surface for the rest of the theme).
  window.cart_open = openSlideCart;
  window.cart_close = closeSlideCart;
  window.cart_build = buildCart;
  window.postToCart = postToCart;
})(window.jQuery);
